// public/data/*.yaml 을 fetch + js-yaml 파싱.
//
// 2026-05-15: 동적 편집되는 SSOT 4종 (features, products, contacts, projects-meta) 은
// /api/yaml/:name proxy 로 fetch — GitHub Contents API 가 main 의 latest 를 반환하고
// Cache-Control: no-store 로 저장 즉시 production 반영. 정적 yaml (softwares, repos,
// products.sync) 은 그대로 /data/X.yaml 정적 serve.

import yaml from 'js-yaml';

async function fetchYaml(path) {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  const text = await res.text();
  return yaml.load(text);
}

// js-yaml은 unquoted ISO 8601 / YYYY-MM-DD 를 Date 객체로 파싱한다.
// React는 Date 객체를 JSX child 로 렌더할 수 없어 error #31 발생 → 항상 string 으로 coerce.
function coerceToIsoDate(v) {
  if (v == null) return null;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

async function fetchJson(path) {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.json();
}

// repos.yaml 형식 → products API와 동일한 shape으로 변환
function transformSoftwares(repos) {
  const list = Array.isArray(repos?.repos) ? repos.repos : [];
  const products = list.map((r) => ({
    id: r.name,
    name: r.desc || r.name,
    category: r.category || 'unknown',
    owner: r.owner || 'TBD',
    signal: r.signal || 'white',
    versions: {
      latest: r.latest && r.latest.tag
        ? { tag: r.latest.tag, published: coerceToIsoDate(r.latest.published) }
        : null,
      planned: Array.isArray(r.planned)
        ? r.planned
            .filter((p) => p && p.tag)
            .map((p) => ({ ...p, published: coerceToIsoDate(p.published) }))
        : [],
    },
  }));
  // 정렬: category 그룹 + signal 순 + name
  const catOrd = ['web', 'app', 'cloud', 'svc', 'lib-docs', 'unknown'];
  const sigOrd = ['green', 'yellow', 'white'];
  products.sort((a, b) =>
    catOrd.indexOf(a.category) - catOrd.indexOf(b.category) ||
    sigOrd.indexOf(a.signal) - sigOrd.indexOf(b.signal) ||
    a.name.localeCompare(b.name),
  );
  return { products, count: products.length };
}

export async function getProducts() {
  // FEAT-009 — dynamic SSOT 승격: 정적 /data/softwares.yaml → /api/yaml/softwares proxy.
  // main commit 즉시 production 반영 (정적 번들 우회 — deploy.yml paths-ignore(yaml) 회피).
  const yamlData = await fetchYaml('/api/yaml/softwares');
  return transformSoftwares(yamlData);
}

export async function getCatalog() {
  // getProjectsIndex() 를 재사용 — dynamic endpoint (/api/projects/list) 우선,
  // 정적 index.json 폴백. 신규 register 직후도 catalog 에 즉시 반영
  // (이전 정적 index.json 만 사용 시 build 시점 이후 등록한 project 가 누락 →
  // FeatureEditor 의 "소속 Project" 드롭다운에 안 보이는 버그 해소).
  const index = await getProjectsIndex();
  // 기존 API 형식과 호환: { catalog: [{ slug, featureId, name, charterUrl }] }
  const catalog = (index.items || []).map((p) => ({
    slug: p.slug,
    featureId: p.slug.toUpperCase(),
    name: p.title,
    charterUrl: `/data/projects/${p.file}`,
  }));
  return { catalog, count: catalog.length };
}

export async function getFeatures() {
  // ADR-001 — features 는 prd-* repo 의 spec/release note 에서 PULL aggregate (derived).
  // push-sync(FEAT-012/031) 대체. aggregator 실패 시 legacy features.yaml 프록시로
  // fallback (과도기 안전장치 — 레거시 backfill 완료 후 제거 예정).
  try {
    const agg = await fetchJson('/api/features/aggregate');
    if (agg && Array.isArray(agg.features)) {
      return {
        features: agg.features,
        exists: true,
        derived: true,
        sources: agg.sources || [],
        warnings: agg.warnings || [],
      };
    }
  } catch (_) {
    /* fallthrough to legacy proxy */
  }
  const data = await fetchYaml('/api/yaml/features');
  return { features: data?.features || [], exists: true, derived: false };
}

// save만 백엔드 호출 — 이제 자체 repo(rederi-agent)에 PR 생성
export async function saveFeatures(features, message) {
  const res = await fetch('/api/feature-map/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ features, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.detail || `save: ${res.status}`);
  return data;
}

// SW/Product/Project 신규 view용 API helpers
export async function getProductInventory() {
  const data = await fetchYaml('/api/yaml/products');
  return { products: data?.products || [], count: (data?.products || []).length };
}

// FEAT-004 Phase 3 v2 — products.yaml 전체 저장 (main 직접 commit).
// 사용처: ProductDetailPage 의 SW 매핑 편집.
export async function saveProducts(products, message) {
  const res = await fetch('/api/products/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ products, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.detail || `save: ${res.status}`);
  return data;
}

// FEAT-017 — 개별 제품 동기화. 그 제품 하나만 재조회하여 sync 캐시 블록을 교체.
export async function syncProduct(id) {
  const res = await fetch(`/api/products/sync?id=${encodeURIComponent(id)}`, { method: 'POST' });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `sync: ${res.status}`);
  return data; // { ok, id, last_run_at, products: [result] }
}

// products.sync.yaml — sync 결과 캐시.
// rederi#281 — Sync 는 main 에 직접 commit 하지만, 정적 번들 /data/products.sync.yaml 은
// release 재배포 전까지 stale → Sync 직후 새로고침 시 옛 데이터로 "초기화"되어 보임.
// /api/yaml/products.sync 동적 프록시(GitHub main, no-store)를 우선 사용해 즉시 반영.
// CF Functions 미배포/프록시 실패 시에만 정적 파일로 폴백.
function normalizeSync(data) {
  const raw = data?.last_run_at;
  const lastRunAt = raw == null ? null
    : raw instanceof Date ? raw.toISOString()
    : String(raw);
  return { lastRunAt, products: Array.isArray(data?.products) ? data.products : [] };
}
export async function getProductsSync() {
  try {
    return normalizeSync(await fetchYaml('/api/yaml/products.sync'));
  } catch (_) {
    /* fallthrough — 정적 번들 폴백 */
  }
  try {
    return normalizeSync(await fetchYaml('/data/products.sync.yaml'));
  } catch {
    return { lastRunAt: null, products: [] };
  }
}

export async function getProjectsIndex() {
  // dynamic endpoint 우선 — 정적 /data/projects/index.json 은 build artifact 라
  // register 직후 stale. /api/projects/list 가 GitHub Contents API 로 실시간 fetch.
  // fallback: 정적 파일 (CF Functions 미배포 환경 / 캐시 폴백)
  try {
    const dyn = await fetchJson('/api/projects/list');
    if (dyn && Array.isArray(dyn.items)) return dyn;
  } catch (_) { /* fallthrough */ }
  return fetchJson('/data/projects/index.json');
}

// 프로젝트 관리 이전 — project-registry(0.project-catalog) 를 읽는 간트 뷰 전용 소스.
// 폴더(active/backlog/done) = status. charter 에서 시작/완료/책임 파싱됨.
// getProjectsIndex() 와 별개 — 매트릭스/카드 연동은 기존 소스를 그대로 사용.
export async function getProjectsCatalog(opts = {}) {
  // fresh=true → 캐시버스팅 query + no-store 로 브라우저·CF edge 5분 캐시 우회.
  // 고유 query param 은 edge cache 키를 바꿔 함수가 project-registry 를 재-read 하게 함.
  const path = opts.fresh ? `/api/projects/catalog?t=${Date.now()}` : '/api/projects/catalog';
  const res = await fetch(path, opts.fresh ? { cache: 'no-store' } : undefined);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  const data = await res.json();
  return { items: Array.isArray(data?.items) ? data.items : [], generatedAt: data?.generated_at || null };
}

// FEAT-018 — 차터 + GitHub Project 진행 현황 + 제품 매핑을 한 번에 읽는다.
// getProjectsCatalog() 의 상위집합. 프로젝트 관리가 GitHub Project 로 확정되어
// '어느 제품의 프로젝트가 지금 어디까지 왔는가' 를 보여주는 화면은 전부 이쪽을 쓴다.
//
// progressAvailable=false 면 GitHub Project 조회 자체가 막힌 것이다 (대개 토큰에
// read:project 스코프 없음). 그래도 items 의 차터 정보(일정·담당)는 유효하므로
// 호출 측은 목록을 그대로 그리고 진행 자리만 비운다.
export async function getProjectsBoard(opts = {}) {
  const path = opts.fresh ? `/api/projects/board?t=${Date.now()}` : '/api/projects/board';
  const res = await fetch(path, opts.fresh ? { cache: 'no-store' } : undefined);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    generatedAt: data?.generated_at || null,
    progressAvailable: data?.progress_available !== false,
    progressError: data?.progress_error || null,
  };
}

// REDERI 가이드 페이지 — 플러그인(rayrndsw/rederi)의 agent/skill/command/module 카탈로그.
export async function getRederiReference() {
  const data = await fetchJson('/api/rederi/reference');
  return {
    agents: Array.isArray(data?.agents) ? data.agents : [],
    skills: Array.isArray(data?.skills) ? data.skills : [],
    modules: Array.isArray(data?.modules) ? data.modules : [],
    commands: Array.isArray(data?.commands) ? data.commands : [],
    generatedAt: data?.generated_at || null,
  };
}

// REDERI 가이드 — "AI에게 물어보기" (Claude API 프록시). catalog 는 grounding 컨텍스트.
export async function askRederi({ question, catalog, history }) {
  const res = await fetch('/api/rederi/ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, catalog, history }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || `요청 실패 (${res.status})`);
  }
  return data.answer || '';
}

export async function getProjectContent(slug) {
  const res = await fetch(`/data/projects/${slug}.md`);
  if (!res.ok) throw new Error(`project ${slug}: ${res.status}`);
  return res.text();
}

// FEAT-005 Phase 5 — Team Contacts SSOT (조직 / 팀 / 멤버).
// 반환:
//   - organizations / teams / members (raw)
//   - membersFlat: { email, name, teamId, teamName, role, grade, ... } flat list
export async function getContacts() {
  try {
    const data = await fetchYaml('/api/yaml/contacts');
    const organizations = Array.isArray(data?.organizations) ? data.organizations : [];
    const teams = Array.isArray(data?.teams) ? data.teams : [];
    const members = Array.isArray(data?.members) ? data.members : [];
    const teamMap = new Map(teams.map((t) => [t.id, t]));
    // 조직 leader 도 flat 에 포함 (변창환 / 신승훈) — 이메일 PK 로 중복 제거
    const flatMap = new Map();
    organizations.forEach((o) => {
      if (o.leader?.email) {
        flatMap.set(o.leader.email, {
          email: o.leader.email,
          name: o.leader.name,
          role: o.leader.role || o.leader.title,
          grade: o.leader.grade,
          phone: o.leader.phone,
          orgId: o.id,
          orgName: o.name,
        });
      }
    });
    members.forEach((m) => {
      const team = teamMap.get(m.team_id);
      flatMap.set(m.email, {
        email: m.email,
        name: m.name,
        role: m.role,
        grade: m.grade,
        phone: m.phone,
        product: m.product,
        teamId: m.team_id,
        teamName: team?.name,
      });
    });
    const membersFlat = Array.from(flatMap.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'ko'),
    );
    return { organizations, teams, members, membersFlat };
  } catch {
    return { organizations: [], teams: [], members: [], membersFlat: [] };
  }
}

// FEAT-005 Phase 5 — Team Contacts 저장. 백엔드가 main 에 직접 commit (2026-05-15).
export async function saveContacts(payload, message) {
  const res = await fetch('/api/contacts/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.detail || `save: ${res.status}`);
  return data;
}

// FEAT-004 Phase 4 — Project lifecycle status SSOT.
export async function getProjectsMeta() {
  try {
    const data = await fetchYaml('/api/yaml/projects-meta');
    // 일정(start/end)은 여기서 오지 않는다 — SSOT 는 project-registry 의 차터이고
    // 콘솔은 /api/projects/board 로 읽는다. 종전에는 Date 객체 렌더 오류를 막으려
    // 날짜를 coerce 했으나, 이제 이 소스에 날짜가 없어 null 만 얹고 있었다.
    return { projects: Array.isArray(data?.projects) ? data.projects : [] };
  } catch {
    return { projects: [] };
  }
}

// FEAT-004 Phase 4 — Project status 저장. 백엔드가 main 에 직접 commit (2026-05-15).
export async function saveProjectsMeta(projects, message) {
  const res = await fetch('/api/projects-meta/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projects, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.detail || `save: ${res.status}`);
  return data;
}

// FEAT-008 / Phase D — 신규 Project 등록 (차터 md + projects-meta.yaml entry 동시 commit).
// 응답: { ok, commitSha, commitUrl, branch, paths } 또는 { ok: false, validation, error, detail }
export async function registerProject(body) {
  const res = await fetch('/api/projects/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  // 400/409 도 body 가 ok:false 형태로 와서 호출측에서 분기. throw 하지 않는다.
  const data = await res.json().catch(() => ({ ok: false, error: `register: ${res.status}` }));
  return data;
}

// FEAT-009 / Phase F — 신규 SW 등록 (softwares.yaml entry 추가 + meta 재계산 + main commit).
// 응답: { ok, name, signal, commitSha, commitUrl, branch } 또는 { ok: false, validation, error, detail }
export async function registerSoftware(body) {
  const res = await fetch('/api/softwares/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({ ok: false, error: `register: ${res.status}` }));
  return data;
}

// FEAT-009 — 등록 전 미리보기 (repo 존재 / desc / latest / signal). commit 안 함.
export async function previewSoftware(name) {
  const res = await fetch(`/api/softwares/preview?name=${encodeURIComponent(name)}`);
  const data = await res.json().catch(() => ({ ok: false, error: `preview: ${res.status}` }));
  return data;
}

// FEAT-011 — 개별 SW 강제 동기화 (Latest / Published / signal 재조회 + main commit).
// 보존: category / owner / planned / desc.
export async function syncSoftware(name) {
  const res = await fetch('/api/softwares/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const data = await res.json().catch(() => ({ ok: false, error: `sync: ${res.status}` }));
  return data;
}

// FEAT-002 매트릭스 뷰 (ADR 결정 2 — 브라우저 직접 조합).
// products / features / softwares 3개 yaml 을 병렬 fetch 후 raw 반환.
// 매트릭스 컴포넌트가 useMemo 로 view 별 join 수행.
export async function getMatrixData() {
  // FEAT-010 — projects(projects-meta) 추가. 관점 3 (프로젝트 × 기능) 의 그룹 메타.
  const [inv, feat, sw, pm] = await Promise.all([
    getProductInventory(),
    getFeatures(),
    getProducts(),
    getProjectsMeta(),
  ]);
  return {
    products: inv.products || [],
    features: feat.features || [],
    softwares: sw.products || [],
    projects: pm.projects || [],
  };
}
